import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Lock, Eye, EyeOff, Mail, Sparkles } from 'lucide-react';
import Button from './Button';
import { useMerchant } from '../hooks/useMerchant';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import '../pages/Login.css';
import '../pages/MerchantLogin.css';
import '../pages/MerchantFirstAccess.css';
import './MerchantMandatoryPasswordModal.css';

const MIN_LEN = 8;

/**
 * Popup bloqueante: troca obrigatória da senha provisória. Não pode fechar sem concluir ou sair da conta.
 */
export function MerchantMandatoryPasswordModal() {
    const navigate = useNavigate();
    const { merchant, login, logout, refreshMerchant } = useMerchant();
    const toast = useToast();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    if (!merchant) {
        return null;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const cur = currentPassword.trim();
        const neu = newPassword.trim();
        const conf = confirmPassword.trim();

        if (!cur) {
            setError('Informe a senha provisória que você recebeu.');
            return;
        }
        if (neu.length < MIN_LEN) {
            setError(`A nova senha deve ter pelo menos ${MIN_LEN} caracteres.`);
            return;
        }
        if (neu !== conf) {
            setError('A confirmação da nova senha não confere.');
            return;
        }
        if (neu === cur) {
            setError('A nova senha deve ser diferente da senha provisória.');
            return;
        }

        setLoading(true);
        try {
            const { error: verifyErr } = await supabase.auth.signInWithPassword({
                email: merchant.email.trim(),
                password: cur,
            });
            if (verifyErr) throw new Error('Senha provisória incorreta.');

            const { error: updateErr } = await supabase.auth.updateUser({ password: neu });
            if (updateErr) throw new Error(updateErr.message);

            const { data: marked, error: markErr } = await supabase.rpc('mark_current_merchant_password_changed');
            if (markErr || marked !== true) throw new Error(markErr?.message || 'Não foi possível marcar a senha como definida.');

            const updated = await refreshMerchant();
            login(updated ?? { ...merchant, mustChangePassword: false });
            toast.success('Tudo certo! Sua senha foi definida. Bem-vindo ao Facilita Pass+.');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Não foi possível salvar a nova senha.';
            setError(msg);
            const missingRpc =
                msg.toLowerCase().includes('could not find the function') ||
                msg.includes('PGRST202') ||
                msg.toLowerCase().includes('404');
            if (missingRpc) {
                console.error('[MerchantMandatoryPasswordModal] Configuração ausente para marcar troca de senha.', err);
                toast.error('Não foi possível concluir a troca de senha agora. Tente novamente ou fale com o suporte.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLeave = () => {
        void logout();
        navigate('/merchant-login', { replace: true });
    };

    return (
        <div
            className="merchant-mandatory-password-overlay"
            role="presentation"
            aria-hidden={false}
        >
            <div
                className="merchant-mandatory-password-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="merchant-mandatory-password-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="login-card merchant-card merchant-first-access-card merchant-mandatory-password-card">
                    <div className="login-header">
                        <div
                            className="logo-icon"
                            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                        >
                            <Store size={32} />
                        </div>
                        <h1 id="merchant-mandatory-password-title">Defina sua senha</h1>
                        <p className="merchant-first-access-welcome">
                            <Sparkles size={16} className="merchant-first-access-welcome-icon" aria-hidden />
                            Por segurança, você precisa substituir a senha provisória por uma senha pessoal antes de usar o
                            portal.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {error && <div className="error-message">{error}</div>}

                        <div className="input-group merchant-first-access-readonly">
                            <div className="input-icon">
                                <Mail size={20} />
                            </div>
                            <input type="email" value={merchant.email} readOnly aria-label="E-mail da conta" />
                        </div>

                        <div className="input-group">
                            <div className="input-icon">
                                <Lock size={20} />
                            </div>
                            <input
                                type={showCurrent ? 'text' : 'password'}
                                placeholder="Senha provisória atual"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowCurrent((v) => !v)}
                                aria-label={showCurrent ? 'Ocultar senha' : 'Mostrar senha'}
                            >
                                {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>

                        <div className="input-group">
                            <div className="input-icon">
                                <Lock size={20} />
                            </div>
                            <input
                                type={showNew ? 'text' : 'password'}
                                placeholder={`Nova senha (mín. ${MIN_LEN} caracteres)`}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                                minLength={MIN_LEN}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowNew((v) => !v)}
                                aria-label={showNew ? 'Ocultar nova senha' : 'Mostrar nova senha'}
                            >
                                {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>

                        <div className="input-group">
                            <div className="input-icon">
                                <Lock size={20} />
                            </div>
                            <input
                                type={showNew ? 'text' : 'password'}
                                placeholder="Confirmar nova senha"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                                minLength={MIN_LEN}
                            />
                        </div>

                        <Button
                            type="submit"
                            variant="primary"
                            fullWidth
                            disabled={loading}
                            className="login-btn"
                            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                        >
                            {loading ? 'Salvando...' : 'Definir senha e continuar'}
                        </Button>
                    </form>

                    <div className="merchant-mandatory-password-footer">
                        <button type="button" className="merchant-mandatory-password-leave" onClick={handleLeave}>
                            Sair e voltar ao login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
